import type { ScheduleTask, ScheduleTaskForChart } from '../../../services/scheduleService';

/** Tasks linked by `dependencies.completionTaskId` (finish-to-start on predecessor completion). */
export type TaskWithCompletionDependency = {
  id: number;
  identifiedDate?: string | null;
  startDate?: string | null;
  taskNumber?: string | null;
  project?: { id: number; name?: string | null; projectNumber?: string | null } | null;
  dependencies?: ScheduleTask['dependencies'] | null;
};

function plannedStartSortTime(t: TaskWithCompletionDependency): number {
  const raw = String(t.identifiedDate ?? t.startDate ?? '').trim();
  if (!raw) return Number.MAX_SAFE_INTEGER;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : Number.MAX_SAFE_INTEGER;
}

function compareTasksStable(a: TaskWithCompletionDependency, b: TaskWithCompletionDependency): number {
  const da = plannedStartSortTime(a);
  const db = plannedStartSortTime(b);
  if (da !== db) return da - db;
  const tn = String(a.taskNumber ?? '').localeCompare(String(b.taskNumber ?? ''));
  if (tn !== 0) return tn;
  return a.id - b.id;
}

/**
 * Within a single group (e.g. one project or one resource), order so every task that depends on
 * another task's completion appears immediately after its predecessor as far as the partial order
 * allows (topological order with stable tie-breaking).
 */
export function sortTasksByCompletionDependency<T extends TaskWithCompletionDependency>(tasks: T[]): T[] {
  if (tasks.length <= 1) return tasks.slice();

  const byId = new Map<number, T>();
  for (const t of tasks) byId.set(t.id, t);

  const inDegree = new Map<number, number>();
  const successors = new Map<number, number[]>();

  for (const t of tasks) {
    inDegree.set(t.id, 0);
  }
  for (const t of tasks) {
    const pred = t.dependencies?.completionTaskId;
    if (pred == null || !Number.isFinite(Number(pred))) continue;
    const p = Number(pred);
    if (!byId.has(p) || p === t.id) continue;
    inDegree.set(t.id, (inDegree.get(t.id) ?? 0) + 1);
    const arr = successors.get(p) ?? [];
    arr.push(t.id);
    successors.set(p, arr);
  }

  const compareIds = (idA: number, idB: number): number =>
    compareTasksStable(byId.get(idA)!, byId.get(idB)!);

  const startNodes = tasks
    .filter((t) => (inDegree.get(t.id) ?? 0) === 0)
    .map((t) => t.id)
    .sort(compareIds);

  /** Process queue: after emitting a task, its newly-ready dependents are prepended so they sit directly under it (not merged behind unrelated tasks by date). */
  let queue = [...startNodes];
  const result: T[] = [];

  while (queue.length > 0) {
    const id = queue.shift()!;
    result.push(byId.get(id)!);
    const newlyReady: number[] = [];
    for (const sid of successors.get(id) ?? []) {
      const d = (inDegree.get(sid) ?? 0) - 1;
      inDegree.set(sid, d);
      if (d === 0) newlyReady.push(sid);
    }
    if (newlyReady.length > 0) {
      newlyReady.sort(compareIds);
      queue = [...newlyReady, ...queue];
    }
  }

  if (result.length < tasks.length) {
    const seen = new Set(result.map((r) => r.id));
    result.push(...tasks.filter((t) => !seen.has(t.id)).sort(compareTasksStable));
  }

  return result;
}

function projectSortKey(t: TaskWithCompletionDependency): string {
  const p = t.project;
  const label = `${p?.projectNumber ?? ''} ${p?.name ?? ''}`.trim();
  return label || '—';
}

/**
 * Full task list: sort projects (same as Gantt buckets), then dependency order within each project.
 */
export function orderScheduleTasksForDisplay(tasks: ScheduleTaskForChart[] | null | undefined): ScheduleTaskForChart[] {
  const list = Array.isArray(tasks) ? tasks : [];
  if (list.length <= 1) return list.slice();

  const byProject = new Map<string, ScheduleTaskForChart[]>();
  const projectKeys: string[] = [];

  for (const t of list) {
    const key = t.project?.id != null ? String(t.project.id) : 'no-project';
    if (!byProject.has(key)) {
      projectKeys.push(key);
      byProject.set(key, []);
    }
    byProject.get(key)!.push(t);
  }

  projectKeys.sort((ka, kb) => {
    const ta = byProject.get(ka)![0];
    const tb = byProject.get(kb)![0];
    return projectSortKey(ta).localeCompare(projectSortKey(tb));
  });

  const out: ScheduleTaskForChart[] = [];
  for (const key of projectKeys) {
    out.push(...sortTasksByCompletionDependency(byProject.get(key)!));
  }
  return out;
}
